import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { FaSearch, FaTh, FaList } from 'react-icons/fa';
import { useSelector } from 'react-redux';
//
import Layout from '../../components/Layout';
import { Input, Title } from '../../components/ui';
import { navigateToLogin } from '../../utils';
import {
  getHiddenCategoryKeys,
  isNameHidden,
} from '../../utils/hiddenCategories';

import ListView from '../../components/ui/ListView';
import { brands } from '../../data/brandsData';

const Container = styled.div`
  display: flex;
  justify-content: center;
  padding: 4rem 0;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem 0;
  }

  @media (max-width: 480px) {
    padding: 2rem 0;
  }
`;

const BrandsContainer = styled.div`
  width: 80%;
  max-width: 1200px;

  @media (max-width: 768px) {
    width: 90%;
    padding: 0 2rem;
  }

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const SearchBar = styled.div`
  display: flex;
  width: 300px;
  height: 50px;
  border-radius: 5px;
  align-items: center;
  border: 2px solid ${(props) => props.theme.colors.subBackground};
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    height: 40px;
  }

  @media (max-width: 480px) {
    height: 35px;
  }
`;

const SearchButton = styled.button`
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 50px;
  height: 50px;
  border: none;
  background: none;
  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }

  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  border-radius: 5px;
  gap: 15px;
  flex-wrap: nowrap;

  @media (max-width: 768px) {
    padding: 15px 0;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 10px 0;
    gap: 8px;
  }
`;

const RowButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 35px;
  height: 35px;
  &:hover {
    opacity: 0.8;
  }
`;

const TotalContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.theme.colors.subBackground};
  padding: 10px;
  font-size: 0.8rem;
`;

const BrandCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  border: 2px solid ${(props) => props.theme.colors.subBackground};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  min-height: 200px;
  position: relative;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    border-color: ${(props) => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    min-height: 150px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    min-height: 120px;
  }
`;

const BrandLogoWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
`;

const BrandLogo = styled.img`
  width: 120px;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 100px;
    height: 60px;
  }

  @media (max-width: 480px) {
    width: 80px;
    height: 50px;
  }
`;

const BrandName = styled.div`
  font-size: 1.1rem;
  font-weight: bold;
  text-align: center;
  color: ${(props) => props.theme.colors.black};

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const BrandItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 1px solid ${(props) => props.theme.colors.subBackground};
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  position: relative;

  &:hover {
    background: ${(props) => props.theme.colors.subBackground};
    transform: translateX(5px);
  }
`;

const BrandItemLogoWrapper = styled.div`
  position: relative;
  display: flex;
  margin-right: 1rem;
`;

const BrandItemLogo = styled.img`
  width: 60px;
  height: 40px;
  object-fit: contain;
  border-radius: 4px;
  cursor: pointer;
`;

const BrandItemName = styled.div`
  font-size: 1rem;
  font-weight: bold;
  color: ${(props) => props.theme.colors.black};
`;

export default function BrandsPage() {
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.userInfo);
  const theme = useTheme();

  const [numColumns, setNumColumns] = useState(4);
  const [keyword, setKeyword] = useState('');
  const [filteredBrands, setFilteredBrands] = useState(brands);
  const [, setCurrentPage] = useState(1);
  // 숨김 처리된 브랜드(Shop By Brand 하위 카테고리) 로고 제외용
  const [hiddenBrandKeys, setHiddenBrandKeys] = useState([]);

  // For now, show all brands without pagination
  const currentBrands = filteredBrands;

  useEffect(() => {
    const loadHiddenKeys = async () => {
      setHiddenBrandKeys(await getHiddenCategoryKeys(userInfo.userId));
    };
    loadHiddenKeys();
  }, [userInfo.userId]);

  useEffect(() => {
    const visibleBrands = brands.filter(
      (brand) => !isNameHidden(brand.name, hiddenBrandKeys),
    );
    if (keyword.trim() === '') {
      setFilteredBrands(visibleBrands);
    } else {
      const filtered = visibleBrands.filter((brand) =>
        brand.name.toLowerCase().includes(keyword.toLowerCase()),
      );
      setFilteredBrands(filtered);
    }
    setCurrentPage(1);
  }, [keyword, hiddenBrandKeys]);

  const handleBrandClick = (brand) => {
    // 브랜드명을 카테고리 URL 키로 변환 (MenuBar buildUrlKey 와 동일 규칙)
    // 예: 'TIGER' -> 'tiger', 'Ottogi Foods' -> 'ottogi-foods'
    const categoryKey = brand.name
      .toLowerCase()
      .replace(/[^a-z\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    const targetLink = `/search?category=${encodeURIComponent(categoryKey)}&page=1`;

    if (userInfo.userId != 0) {
      navigate(targetLink);
    } else {
      navigateToLogin(navigate, targetLink);
    }
  };

  const renderBrandItem = ({ item }) => {
    if (numColumns === 1) {
      return (
        <BrandItem onClick={() => handleBrandClick(item)}>
          <BrandItemLogoWrapper>
            <BrandItemLogo src={item.logo} alt={item.name} />
          </BrandItemLogoWrapper>
          <BrandItemName>{item.name}</BrandItemName>
        </BrandItem>
      );
    }

    return (
      <BrandCard onClick={() => handleBrandClick(item)}>
        <BrandLogoWrapper>
          <BrandLogo src={item.logo} alt={item.name} />
        </BrandLogoWrapper>
        <BrandName>{item.name}</BrandName>
      </BrandCard>
    );
  };

  return (
    <Layout>
      <Container>
        <BrandsContainer>
          <TitleContainer>
            <Title>Shop by Brand</Title>
            <SearchBar>
              <Input
                placeholder='Search brands...'
                value={keyword}
                onChange={(e) => {
                  setKeyword(e);
                }}
              />
              <SearchButton>
                <FaSearch size={20} color={theme.colors.primaryText} />
              </SearchButton>
            </SearchBar>
          </TitleContainer>

          <TitleContainer>
            <Row>
              <RowButton
                onClick={() => {
                  setNumColumns(4);
                }}
              >
                <FaTh size={35} />
              </RowButton>
              <RowButton
                onClick={() => {
                  setNumColumns(1);
                }}
              >
                <FaList size={35} />
              </RowButton>
            </Row>
          </TitleContainer>

          {keyword && (
            <TotalContainer>
              {`${filteredBrands.length} brands found.`}
            </TotalContainer>
          )}

          {currentBrands.length > 0 ? (
            <ListView
              data={currentBrands}
              renderItem={renderBrandItem}
              numColumns={(width) =>
                numColumns === 1 ? 1 : width <= 480 ? 2 : width <= 768 ? 3 : 4
              }
              rowGap={'30px'}
              columnGap={'20px'}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>No brands found.</p>
            </div>
          )}
        </BrandsContainer>
      </Container>
    </Layout>
  );
}
